import { ColorSource, Container, TextStyle } from "pixi.js";
import ICreate from "../interfaces/ICreate.ts";
import IDestroy from "../interfaces/IDestroy.ts";
import { gsap } from "gsap";
import Mark from "../marks/Mark.ts";
import MarkText from "./MarkText.ts";
import DecodingModel from "../models/DecodingModel.ts";
import TimelineManager from "@/managers/TimelineManager.ts";

export default class SplitText extends Container implements ICreate, IDestroy {
    private _text: string;
    private _textFields: MarkText[];
    private _highlightedTextFields: MarkText[];
    private chars: string[];
    private textStyleHighlight: TextStyle;

    public static readonly DECODING_DEFAULT: number = 200;
    public static readonly DECODING_SINGLE_SYLLABIC: number = 200;
    public static readonly DECODING_MULTI_SYLLABIC: number = 138;

    public fontSize: number;
    public fontColor: ColorSource = "#363538";
    public textStyle: TextStyle;

    public get numChars(): number {
        return this.chars.length;
    }

    public get textFields(): MarkText[] {
        return this._textFields;
    }

    public get text(): string {
        return this._text;
    }

    public set text(value: string) {
        this._text = value;
        this.chars = this._text.split("");
        this.destroy();
        this.create();
    }

    constructor(text: string) {
        super();
        this._text = text;
        this._init();
    }

    private _init(): void {
        this.chars = this._text.split("");
        this.fontSize = SplitText.DECODING_DEFAULT;
    }

    private createTextFields(): void {
        let newWidth = 0;
        this._textFields = [];
        let curText: MarkText;
        let curX = 0;
        let len = this.chars.length;
        for (let i = 0; i < len; i++) {
            curText = new MarkText(this.chars[i], this.textStyle);
            curText.label = `char_${i}`;
            curText.x = curX;
            curX += curText.width;
            this.addChild(curText);
            this._textFields.push(curText);
            newWidth += curText.width;
        }
    }

    private createTextFieldsHighlight(): void {
        let newWidth = 0;
        this._highlightedTextFields = [];
        let curText: MarkText;
        let curX = 0;
        let len = this.chars.length;
        for (let i = 0; i < len; i++) {
            curText = new MarkText(this.chars[i], this.textStyleHighlight);
            curText.label = `char_${i}`;
            curText.x = curX;
            curX += curText.width;
            curText.alpha = 0;
            curText.visible = false;
            this.addChild(curText);
            this._highlightedTextFields.push(curText);
            newWidth += curText.width;
        }
    }

    private createTextStyles(): void {
        if (this.textStyle == undefined) {
            this.textStyle = new TextStyle({
                fontFamily: DecodingModel.getInstance().fontFamily,
                fontSize: this.fontSize,
                fill: this.fontColor,
                align: "center",
                wordWrap: false,
            });
        }
        this.textStyleHighlight = new TextStyle({
            fontFamily: DecodingModel.getInstance().fontFamily,
            fontSize: this.fontSize,
            fill: "#00E2FF",
            align: "center",
            wordWrap: false,
        });
    }

    private destroyTextFields(): void {
        if (this._textFields == null) return;
        let curText: MarkText;
        let len = this._textFields.length;
        for (let i = 0; i < len; i++) {
            curText = this._textFields[i];
            this.removeChild(curText);
            curText.destroy();
        }
        this._textFields = null;
    }

    private destroyTextFieldsHighlight(): void {
        if (this._highlightedTextFields == null) return;
        let curText: MarkText;
        let len = this._highlightedTextFields.length;
        for (let i = 0; i < len; i++) {
            curText = this._highlightedTextFields[i];
            this.removeChild(curText);
            curText.destroy();
        }
        this._highlightedTextFields = null;
    }

    private highlightByIndexComplete = (completeHandler: Function): void => {
        if (completeHandler) {
            completeHandler();
        }
    };

    private unhighlightByIndexComplete = (index: number, completeHandler: Function): void => {
        this._highlightedTextFields[index].visible = false;
        if (completeHandler) {
            completeHandler();
        }
    };

    public create(): void {
        this.createTextStyles();
        this.createTextFields();
        this.createTextFieldsHighlight();
    }

    public destroy(): void {
        this.destroyTextFieldsHighlight();
        this.destroyTextFields();
        this.removeChildren();
    }

    public fadeFieldToAlphaByIndex(index: number, alpha: number, duration = 0.5, ease = "power4.in"): void {
        TimelineManager.to(this._textFields[index], { duration: duration, alpha: alpha, ease: ease });
    }

    public getTextByIndex(index: number): MarkText {
        return this._textFields[index];
    }

    public highlightByIndex(index = 0, completeHandler?: Function): void {
        this._highlightedTextFields[index].alpha = 0;
        this._highlightedTextFields[index].visible = true;
        TimelineManager.to(this._highlightedTextFields[index], {
            alpha: 1,
            duration: 0.5,
            onComplete: this.highlightByIndexComplete,
            onCompleteParams: [completeHandler],
        });
        if (this._textFields[index].children.length > 0) {
            const firstChild = this._textFields[index].children[0];
            if (firstChild instanceof Mark) {
                firstChild.highlight();
            }
        }
    }

    public unhighlightByIndex(index = 0, completeHandler?: Function): void {
        this._highlightedTextFields[index].alpha = 0;
        this._highlightedTextFields[index].visible = true;
        TimelineManager.to(this._highlightedTextFields[index], {
            alpha: 0,
            duration: 0.5,
            onComplete: this.unhighlightByIndexComplete,
            onCompleteParams: [index, completeHandler],
        });
        if (this._textFields[index].children.length > 0) {
            const firstChild = this._textFields[index].children[0];
            if (firstChild instanceof Mark) {
                firstChild.unhighlight();
            }
        }
    }
}
